import OpenAI from 'openai';
import { API_KEYS } from '../../utils/api-config';

const openai = new OpenAI({
  apiKey: API_KEYS.OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export interface AIRateResponse {
  currency: string;
  officialRate: number;
  parallelRate: number;
  source: string;
  confidence: number;
  timestamp: Date;
}

export async function fetchLiveRatesWithOpenAI(): Promise<AIRateResponse[]> {
  try {
    if (!API_KEYS.OPENAI_API_KEY || API_KEYS.OPENAI_API_KEY === 'your-openai-key') {
      console.warn('OpenAI API key not configured');
      throw new Error('OpenAI API key not configured');
    }

    const currentDate = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Lagos'
    });

    const response = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [{
        role: "system",
        content: `You are a real-time financial data API with web search capabilities. Your task is to fetch CURRENT, LIVE exchange rates for Nigerian Naira (NGN).

DATA SOURCES (in priority order):
1. Google Finance - Search "USD NGN", "EUR NGN" for live rates
2. XE.com - Current converter rates
3. FMDQ OTC Nigeria - Official I&E window rates
4. Bloomberg/Reuters - Market rates
5. Nigerian forex bureaus - Parallel market rates

RATE TYPES:
- Official Rate (CBN I&E Window): Central Bank managed rate for importers/exporters
- Parallel Market Rate: Lagos/Abuja street/bureau de change rates (typically 8-15% premium)

VALIDATION RULES (as of February 2026):
- USD/NGN Official: 1450-1470, Parallel: 1600-1650
- EUR/NGN Official: 1580-1620, Parallel: 1750-1820
- GBP/NGN Official: 1840-1880, Parallel: 2030-2100

OUTPUT FORMAT (JSON only):
{
  "rates": [
    {
      "currency": "USD",
      "officialRate": 1460.50,
      "parallelRate": 1620.00,
      "source": "Google Finance + XE.com",
      "confidence": 0.95
    }
  ],
  "fetchedAt": "${currentDate}",
  "dataQuality": "live"
}`
      }, {
        role: "user",
        content: `Search and fetch CURRENT Nigerian Naira exchange rates as of ${currentDate}.

REQUIRED CURRENCIES: USD, EUR, GBP, CAD, AUD, XOF

For EACH currency provide:
1. CBN Official Rate (I&E Window)
2. Parallel Market Rate (Lagos street rate)
3. Data source(s) used
4. Confidence score (0.0-1.0)

CRITICAL: Use your web search to get ACTUAL current rates from:
- Google: Search "USD to NGN exchange rate"
- XE.com: Check live converter
- FMDQ: Official Nigerian forex platform

Return ONLY valid JSON. No markdown, no explanations, just the JSON object.`
      }],
      temperature: 0.2,
      response_format: { type: "json_object" },
      max_tokens: 2000
    });

    const content = response.choices[0].message.content || '{"rates": []}';

    if (!content) {
      throw new Error('Empty response from OpenAI');
    }

    const parsed = JSON.parse(content);
    const rates = parsed.rates || [];

    if (!Array.isArray(rates) || rates.length === 0) {
      throw new Error('Invalid or empty rates array from OpenAI');
    }

    console.log(`OpenAI API returned ${rates.length} currency rates`);

    return rates.map((rate: any) => ({
      currency: rate.currency,
      officialRate: Number(rate.officialRate),
      parallelRate: Number(rate.parallelRate),
      source: rate.source || 'OpenAI GPT-4',
      confidence: Number(rate.confidence) || 0.85,
      timestamp: new Date()
    }));
  } catch (error) {
    console.error('Error fetching rates with OpenAI:', error);
    throw error;
  }
}

export async function fetchLiveRatesWithGemini(): Promise<AIRateResponse[]> {
  try {
    const apiKey = import.meta.env.VITE_GOOGLE_GEMINI_API_KEY;

    if (!apiKey || apiKey === 'your_gemini_api_key_here') {
      console.warn('Google Gemini API key not configured');
      throw new Error('Google Gemini API key not configured');
    }

    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `You are a real-time financial data API. Search for and provide CURRENT, LIVE Nigerian Naira (NGN) exchange rates as of ${currentDate}.

CRITICAL INSTRUCTIONS:
1. Search Google, XE.com, Bloomberg, and FMDQ for REAL, CURRENT rates
2. Provide ACTUAL market rates, not estimates or historical data
3. Return rates for: USD, EUR, GBP, CAD, AUD, XOF

DATA REQUIREMENTS:
- CBN Official Rates (I&E Window): Current rates from Central Bank of Nigeria
- Parallel Market Rates: Current Lagos/Abuja street rates (typically 8-15% higher)
- As of February 2026: USD/NGN official ~1460, parallel ~1620

RESPONSE FORMAT (JSON ONLY):
{
  "rates": [
    {
      "currency": "USD",
      "officialRate": 1460.50,
      "parallelRate": 1620.00,
      "source": "Google Finance + FMDQ",
      "confidence": 0.95
    }
  ],
  "timestamp": "${new Date().toISOString()}",
  "dataQuality": "real-time"
}

Search these sources NOW and return ONLY valid JSON with current rates. No explanations.`
            }]
          }],
          generationConfig: {
            temperature: 0.1,
            topK: 1,
            topP: 0.95,
            maxOutputTokens: 4096,
            responseMimeType: "application/json"
          }
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Gemini API error: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

    if (!text) {
      throw new Error('Empty response from Gemini API');
    }

    // Try to parse as JSON directly first
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      // If direct parse fails, extract JSON from text
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in Gemini response');
      }
      parsed = JSON.parse(jsonMatch[0]);
    }

    const rates = parsed.rates || [];

    if (rates.length === 0) {
      throw new Error('No rates returned from Gemini API');
    }

    console.log(`Gemini API returned ${rates.length} currency rates`);

    return rates.map((rate: any) => ({
      currency: rate.currency,
      officialRate: Number(rate.officialRate),
      parallelRate: Number(rate.parallelRate),
      source: rate.source || 'Gemini AI',
      confidence: Number(rate.confidence) || 0.85,
      timestamp: new Date()
    }));
  } catch (error) {
    console.error('Error fetching rates with Gemini:', error);
    throw error;
  }
}

export async function getAIEnhancedRates(): Promise<{
  official: AIRateResponse[];
  parallel: AIRateResponse[];
}> {
  const results: AIRateResponse[] = [];
  const errors: string[] = [];

  // Try OpenAI first (usually more reliable with web search)
  try {
    console.log('Fetching rates from OpenAI GPT-4...');
    const openaiRates = await fetchLiveRatesWithOpenAI();
    if (openaiRates && openaiRates.length > 0) {
      console.log(`✓ OpenAI returned ${openaiRates.length} rates`);
      results.push(...openaiRates);
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    console.warn('✗ OpenAI rates failed:', errorMsg);
    errors.push(`OpenAI: ${errorMsg}`);
  }

  // Try Gemini as backup
  try {
    console.log('Fetching rates from Google Gemini...');
    const geminiRates = await fetchLiveRatesWithGemini();
    if (geminiRates && geminiRates.length > 0) {
      console.log(`✓ Gemini returned ${geminiRates.length} rates`);
      results.push(...geminiRates);
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    console.warn('✗ Gemini rates failed:', errorMsg);
    errors.push(`Gemini: ${errorMsg}`);
  }

  // Check if we got any results
  if (results.length === 0) {
    const errorSummary = errors.join('; ');
    console.error('All AI rate sources failed:', errorSummary);
    throw new Error(`All AI rate sources failed: ${errorSummary}`);
  }

  console.log(`Total AI rate responses: ${results.length}`);

  // Aggregate rates by currency
  const currencyMap = new Map<string, {
    official: number[];
    parallel: number[];
    sources: string[];
    confidences: number[];
  }>();

  results.forEach(rate => {
    if (!currencyMap.has(rate.currency)) {
      currencyMap.set(rate.currency, {
        official: [],
        parallel: [],
        sources: [],
        confidences: []
      });
    }
    const entry = currencyMap.get(rate.currency)!;
    entry.official.push(rate.officialRate);
    entry.parallel.push(rate.parallelRate);
    entry.sources.push(rate.source);
    entry.confidences.push(rate.confidence);
  });

  const officialRates: AIRateResponse[] = [];
  const parallelRates: AIRateResponse[] = [];

  currencyMap.forEach((rates, currency) => {
    // Use weighted average based on confidence scores
    const totalConfidence = rates.confidences.reduce((a, b) => a + b, 0);

    const avgOfficial = rates.official.reduce((sum, rate, idx) => {
      const weight = rates.confidences[idx] / totalConfidence;
      return sum + (rate * weight);
    }, 0);

    const avgParallel = rates.parallel.reduce((sum, rate, idx) => {
      const weight = rates.confidences[idx] / totalConfidence;
      return sum + (rate * weight);
    }, 0);

    const avgConfidence = totalConfidence / rates.confidences.length;
    const sourcesUsed = [...new Set(rates.sources)].join(' + ');

    const rateData = {
      currency,
      officialRate: Math.round(avgOfficial * 100) / 100,
      parallelRate: Math.round(avgParallel * 100) / 100,
      source: `AI-Enhanced: ${sourcesUsed}`,
      confidence: Math.round(avgConfidence * 100) / 100,
      timestamp: new Date()
    };

    officialRates.push(rateData);
    parallelRates.push({ ...rateData });

    console.log(`${currency}: Official ₦${rateData.officialRate}, Parallel ₦${rateData.parallelRate} (${sourcesUsed})`);
  });

  console.log(`Successfully aggregated rates for ${officialRates.length} currencies`);

  return { official: officialRates, parallel: parallelRates };
}

export async function validateRatesWithAI(rates: any[]): Promise<boolean> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [{
        role: "system",
        content: "You are a financial data validator. Check if the provided exchange rates are realistic and current for Nigerian Naira."
      }, {
        role: "user",
        content: `Are these exchange rates realistic for ${new Date().toLocaleDateString()}?
        ${JSON.stringify(rates, null, 2)}

        Respond with JSON: { "valid": true/false, "reason": "explanation" }`
      }],
      temperature: 0.1,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"valid": false}';
    const result = JSON.parse(content);

    return result.valid === true;
  } catch (error) {
    console.error('Error validating rates:', error);
    return false;
  }
}
